// Shared row behavior for the BatchView data table and the
// BatchItemAlternateStores (cascaded store) data table. Keeping these helpers
// in one place keeps the focal rows and alternate-store rows behaving
// identically and guards against the two tables drifting apart.
import Rules from '@/axios/rules-management'

// How far over the original promo price a compliance-enforced TPR may be priced. Flat by
// agreement with ARG rather than the promo's variance, which is $0.05 on every TPR-type
// compliance promo anyway. Must match COMPLIANCE_TOLERANCE in tpr-pro-api's compliance.py,
// which refuses the same save on the server.
export const COMPLIANCE_TOLERANCE = 0.05

export const tprItemRow = {
  computed: {
    srpRules() {
      const rules = []
      const valueRequired = v => !!v || 'SRP Required'
      rules.push(valueRequired)
      const positiveValue = v => parseFloat(v) >= 0 || 'Invalid SRP'
      rules.push(positiveValue)
      return rules
    },
    multiplierRules() {
      const rules = []
      const valueRequired = v => !!v || 'Multiplier Required'
      rules.push(valueRequired)
      const positiveValue = v => v > 0 || 'Invalid Multiplier'
      rules.push(positiveValue)
      return rules
    },
    blockNonCompliantSaves() {
      return this.tprProTenants && !this.isTprProRetailUser
    },
    complianceBatch() {
      return this.selectedBatch
    }
  },
  methods: {
    // An excluded row still receives a propagated price -- it costs no extra logic and the
    // row is not going to a shelf -- but it must not be *judged*. The compliance rules exist
    // to keep a bad price off a tag, and an excluded row produces no tag, so refusing a whole
    // save over one would block a price nobody is setting. A blank or absent reason means
    // included: the engine writes '' for a live row, and a row built without the field is
    // not excluded.
    rowIsExcluded(row) {
      return row?.excluded != null && row.excluded !== ''
    },
    srpExceedsBaseRetail(item) {
      // AWG does not maintain perishable base retails reliably, so the base-retail ceiling
      // is not trustworthy for perishable batches — exempt them from this gate.
      if (String(this.complianceBatch?.batch_type || '').toUpperCase() === 'PERISHABLE') return false
      const multiplier = Number(item.multiplier) || 1
      if (multiplier <= 0) return false
      const retail = Number(item.retail_price)
      const promo = Number(item.promotion_price)
      if (!(retail > 0) || !(promo > 0)) return false
      return (promo / multiplier) > retail
    },
    srpComplianceLimit(item) {
      const base = this.isTprProRetailUser ? item.original_promotion_price : item.rules_promotion_price
      // item.variance is blank on store-group rows; the backend defaults it to the nickel tolerance, so mirror that.
      const variance = parseFloat(item.variance)
      const tolerance = Number.isNaN(variance) ? 0.05 : variance
      return (parseFloat(base) + tolerance).toFixed(2) * item.multiplier
    },
    srpIsCompliant(item) {
      if (item.enforce_compliance == false) return true
      return !(parseFloat(item.promotion_price) > this.srpComplianceLimit(item))
    },
    // Max SRP ceiling: the mandatory pass-through, or the required retail on a
    // compliance-enforced TPR (see passPercentMaxSRP). Lived on BatchView only, so cascaded
    // rows were never checked against it at all -- the child table could save any SRP
    // without a word. `selectedBatch` is a prop on the cascaded table and data on BatchView;
    // both resolve here.
    passPercentIsCompliant(item) {
      const nonPerishable = String(this.complianceBatch?.batch_type || '').toUpperCase() === 'NON PERISHABLE'
      if (!(this.$auth.tenant === 'alliance-retail-group' && this.isTprProRetailUser && item.retail_price != 0 && nonPerishable)) return true
      const maxSRP = this.passPercentMaxSRP(item)
      if (maxSRP <= 0) return true
      return (parseFloat(item.promotion_price) / item.multiplier) <= maxSRP
    },
    // Per-unit ceiling, split on enforced compliance. A compliance-enforced TPR is held to its
    // required retail -- the original promo price plus the tolerance -- and the pass-through
    // does not apply to it. Every other TPR is held to the pass-through alone. Taking the
    // greater of the two on every row let a non-compliance TPR climb to the original promo
    // price, and a compliance TPR climb past its required retail to the pass-through.
    // This picks the ceiling only; it does not decide whether a row is judged at all. That
    // scope -- ARG, retail user, Non-Perishable, a base retail -- is passPercentIsCompliant's,
    // and every caller goes through it first, so call that rather than this.
    passPercentMaxSRP(item) {
      if (this.isCappedAtRequiredRetail(item)) {
        // toFixed: 5.89 + 0.05 is 5.9399... in floating point, which would refuse $5.94
        return Number((parseFloat(item.original_promotion_price) + COMPLIANCE_TOLERANCE).toFixed(2))
      }
      return parseFloat(item.max_srp_for_passthrough_percent) || 0
    },
    // A compliance TPR with no original promo price has no required retail to hold it to, so
    // it falls back to the pass-through like any other row.
    isCappedAtRequiredRetail(item) {
      return !!item.enforce_compliance && (parseFloat(item.original_promotion_price) || 0) > 0
    },
    // The ceiling passPercentMaxSRP applied, in the business's words, for messages.
    maxSrpRule(item) {
      return this.isCappedAtRequiredRetail(item) ? 'required retail' : 'mandatory pass-through'
    },
    // One phrase for a message covering several rows, naming both rules when they are mixed.
    maxSrpRulesFor(rows) {
      return [...new Set(rows.map(row => this.maxSrpRule(row)))].sort().join(' or ')
    },
    // Trim to 2 decimals without rounding, so an SRP is never nudged up a cent. This lived
    // on BatchView, which meant the cascaded table never trimmed at all and could store a
    // sub-cent price that only looked clean because the reload formatter rounds it.
    truncateToTwoDecimals(value) {
      if (value === '' || value === null || value === undefined) {
        return value
      }
      const stringValue = String(value)
      const decimalIndex = stringValue.indexOf('.')
      const truncated = decimalIndex > -1 ? 
        stringValue.substring(0, decimalIndex + 3) 
        : stringValue
      return parseFloat(truncated).toFixed(2)
    },
    // Lazily load per-promo compliance data so both callers fetch it identically.
    async ensureEnforcedPromos(item, batchId, storeId) {
      if (this.isTprProRetailUser && item.enforce_compliance && item.enforce_compliance_promos == undefined) {
        const res = await Rules.getEnforcedPromos(batchId, storeId, item.item_id)
        item.enforce_compliance_promos = res.data
      }
    },
    // Single source of truth for Net GP%; caller passes srp/multiplier and pre-loads promos.
    calculateNetGrossProfit(item, srp, multiplier, isPerishable = false) {
      if (multiplier <= 0 || srp <= 0) {
        return 0.00
      }
      let allowances = Number(item.amap_allowance) + Number(item.dsd_oi_allowance) + Number(item.billback_allowance) + Number(item.tpr_billback_allowance) + Number(item.edlc_billback_allowance) + Number(item.vpap_tpr_billback_allowance) + Number(item.accrual_tpr_billback_allowance)
      // Exclude Early Buy Allowance for ARG non-perishable batches only
      if (this.$auth.tenant != 'alliance-retail-group' || isPerishable) {
        allowances += Number(item.early_buy_allowance)
      }
      let scanAllowances = Number(item.scan_allowance) + Number(item.edlc_scan_allowance) + Number(item.tpr_scan_allowance)
      const nonCompliantList = []
      const catToCol = {
        "TPR Scan - ePay": "tpr_scan_epay_allowance",
        "TPR Scan - Compete": "tpr_scan_compete_allowance",
        "EDLC Scan - ePay": "edlc_scan_epay_allowance",
        "EDLC Scan - Compete": "edlc_scan_compete_allowance"
      }
      if (this.isTprProRetailUser) {
        item.enforce_compliance_promos?.forEach((promo) => {
          const col = catToCol[promo.promo_category_name]
          // per-unit price vs the per-promo threshold + variance (item.variance is blank on store-group rows)
          if ((srp / multiplier) <= (parseFloat(promo.promotion_price) + (parseFloat(promo.variance ?? item.variance) || 0))) {
            scanAllowances += item[col] ?? 0
          } else {
            nonCompliantList.push(promo.promo_category_name)
          }
        })
        this.$set(item, 'nonCompliantList', nonCompliantList)
      } else {
        scanAllowances += Number(item.tpr_scan_compete_allowance) + Number(item.tpr_scan_epay_allowance) + Number(item.edlc_scan_compete_allowance) + Number(item.edlc_scan_epay_allowance)
      }
      let cost_source = item.cost
      if (this.tprProTenants && item.case_freight != null) {
        cost_source = item.case_freight
      }
      const unitCost = ((Number(cost_source) - allowances) / Number(item.qty_per_case)) - scanAllowances
      item.net_unit_cost = Math.round(unitCost * 100) / 100
      const grossProfit = (((Number(srp) / Number(multiplier)) - unitCost) / (Number(srp) / Number(multiplier))) * 10000
      return Math.round(grossProfit) / 100
    },
    findMinDiscountRule(item) {
      if (!this.hasRules(item.rules_applied)) return null
      let rules
      try {
        rules = typeof item.rules_applied === 'string' ? JSON.parse(item.rules_applied) : item.rules_applied
      } catch (err) {
        return null
      }
      if (!Array.isArray(rules)) return null
      const rule = rules.find(r => r && r.name && r.name.toLowerCase().includes('discount'))
      if (!rule) return null
      const name = String(rule.name || '').toLowerCase()
      const label = String(rule.label || '').toLowerCase()
      const isPercent = name.includes('pct') || name.includes('percent') || label.includes('percent') || label.includes('%')
      return { rule, isPercent }
    },
    getExclusionReason(item) {
      if (!item) return ''
      const included = item.excluded === '' || item.excluded === null
      if (included) return ''

      // Mirror primary's reason on cascaded rows; generic note otherwise.
      if (item.exclusion_reason) return item.exclusion_reason

      // This is not implemented as a rule, so have to check for seperately outside of below exclusion value check
      if (this.srpExceedsBaseRetail(item)) return 'TPR SRP exceeds Base Retail'

      // When rules are applied, an exclusion string for the exclusion reason is added to the item's data. Use that as a base
      //  to return a more detailed string for the tooltip
      switch (item.excluded) {
        case 'minimum_discount':
          return 'Excluded by Min Discount $ Rule'

        case 'minimum_percentage':
          return 'Excluded by Min Discount % Rule'

        case 'manual':
          return 'Un-included'

        case 'promotion_price':
          return 'Promotion Price is not valid'

        default:
          return item.excluded
      }
    },
    hasRules(rulesApplied) {
      if (!rulesApplied || rulesApplied === '[]') return false
      try {
        const parsed = JSON.parse(rulesApplied)
        return Array.isArray(parsed) && parsed.length > 0
      } catch (err) {
        console.error('Invalid JSON in rules_applied:', err)
        return false
      }
    },
    parseRules(rulesApplied) {
      // Friendly labels for engine marker pseudo-rules.
      const markerLabels = { 'Non-Subscriber': 'Not a Price Pro store' }
      try {
        const parsedRules = typeof rulesApplied === 'string' ? JSON.parse(rulesApplied) : rulesApplied
        return parsedRules.map(rule => {
          if (rule.name && rule.name.toLowerCase().includes('discount')) {
            rule.displayValue = `$${rule.value}`
          } else if (rule.name && /pct|percent/.test(rule.name.toLowerCase())) {
            rule.displayValue = `${rule.value}%`
          } else {
            rule.displayValue = rule.value
          }
          rule.displayLabel = markerLabels[rule.name] || rule.display_name || rule.name
          return rule
        })
      } catch (err) {
        console.error('Failed to parse rules_applied:', err)
        return []
      }
    },
    getRuleDisplay(ruleKey) {
      const ruleMapping = {
        'ad_group': 'Ad Group',
        'store': 'Store',
        'department': 'Department',
        'category': 'Category',
        'vendor': 'Vendor'
      }
      return ruleMapping[ruleKey] || ruleKey
    },
    getWarningColor(item) {
      if (this.changes.some(change => change.id === item.id) && this.blockNonCompliantSaves) {
        return 'white'
      }
      return 'red'
    },
    getCompetitorPrice(item, competitor) {
      const value = item[`comp_pricing_${competitor}`]
      return value !== undefined ? value : 'N/A'
    },
    getPriceComparisonColor(item, competitor) {
      const competitorPrice = this.getCompetitorPrice(item, competitor)
      if (!competitorPrice || competitorPrice === 'N/A' || !item.promotion_price) return 'grey'
      const compprice = parseFloat(competitorPrice)
      const userprice = parseFloat(item.promotion_price) / parseFloat(item.multiplier || 1)
      if (compprice === userprice) return 'grey'
      if (userprice > compprice) return 'red'
      return 'green'
    },
    getPriceComparisonIcon(item, competitor) {
      const competitorPrice = this.getCompetitorPrice(item, competitor)
      if (!competitorPrice || competitorPrice === 'N/A' || !item.promotion_price) return 'mdi-minus'
      const compprice = parseFloat(competitorPrice)
      const userprice = parseFloat(item.promotion_price) / parseFloat(item.multiplier || 1)
      if (compprice === userprice) return 'mdi-equal'
      if (userprice > compprice) return 'mdi-arrow-up'
      return 'mdi-arrow-down'
    },
    getPriceComparisonText(item, competitor) {
      const competitorPrice = this.getCompetitorPrice(item, competitor)
      if (!competitorPrice || competitorPrice === 'N/A' || !item.promotion_price) return 'No price comparison available'
      const compprice = parseFloat(competitorPrice)
      const userprice = parseFloat(item.promotion_price) / parseFloat(item.multiplier || 1)
      if (compprice === userprice) return `Prices are equal to $${compprice}`
      if (userprice > compprice) {
        const diff = ((userprice - compprice) / compprice * 100).toFixed(2)
        return `Price ${diff}% More Expensive than Competitor price of $${compprice}`
      }
      const diff = ((compprice - userprice) / compprice * 100).toFixed(2)
      return `Price ${diff}% Cheaper than Competitor price of $${compprice}`
    }
  }
}
